import { useMemo } from "react";
import { CalendarEvent } from "shared";

import { useOrgContext } from "@/lib/context/OrgContext";
import { useCalendarEvents } from "@/lib/data/calendarEvents";

export interface UseNextEventByTagOptions {
  groupId: string;
  tags: string[];
  orgUid?: string;
}

/**
 * Hook to fetch the next upcoming event filtered by tags and group
 *
 * @param options - Options including groupId, tags, and optional orgUid
 * @returns Object with event (or null), loading state, and error
 *
 * @example
 * const { event, loading, error } = useNextEventByTag({
 *   groupId: '400G',
 *   tags: ['ceremony', 'court-of-honor']
 * });
 */
export function useNextEventByTag({
  groupId,
  tags,
  orgUid: providedOrgUid,
}: UseNextEventByTagOptions): {
  event: CalendarEvent | null;
  loading: boolean;
  error: string | null;
} {
  const { orgUid: contextOrgUid } = useOrgContext();
  const orgUid = providedOrgUid ?? contextOrgUid ?? undefined;

  const filterOptions = useMemo(
    () => ({
      groupIds: [groupId],
      tags,
      startDate: new Date(), // Only future events
      isPublic: true, // Required for unauthenticated access per security rules
    }),
    [groupId, tags],
  );

  const {
    data: events,
    loading,
    error,
  } = useCalendarEvents(orgUid, filterOptions);

  // Return first event (already sorted by startDate ascending)
  const event = events && events.length > 0 ? events[0] : null;

  return { event, loading, error };
}
